/**
 * The Tasks tab on the phone (docs/mobile-design.md §4.11): header with a yellow + (the full new-task form), a search box
 * (title, description, tags, project, building, assignee) and filter chips (All · Overdue · Today · Upcoming · No date ·
 * Done). Complete by holding the round checkbox (or a swipe right), with Undo; Done reopens the same way.
 * Checklists, issues and acknowledgements keep their rows under the list.
 * t333u: a task with subtasks shows "done/total" and a chevron on the right; tapping it lists the subtasks inline
 * (hold to complete, tap to open the subtask's own sheet), kept open for the session. Same as the app.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { useScrollRestoration } from '@/lib/scrollRestore';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { CalendarDays, ChevronDown, ChevronRight, Lock, Plus, Search, ShieldCheck, Siren, X } from 'lucide-react';
import { CHECKLIST_DUE_LABELS, CHECKLIST_DUE_PRESETS, checklistDueEmpty, checklistDueMatches, checklistDueRange, MY_CHECKLIST_FILTERS, checklistCounts, checklistMatches, sortChecklists, type ChecklistRun, type MyChecklistFilter, MY_TASK_FILTERS, PRIORITY_LABEL, filterMyTasks, isIssueAssignee, myTaskCounts, taskDueLabel, taskMatches, type MyTaskFilter, type SearchableTask, type TaskListItem } from '@tradeoutlet/shared';
import { useExpandedTasks, useMyChecklists, useMyIssues, useMyDoneTasks, useMyOpenTasks, useSetTaskDone, useSubtasks } from '@tradeoutlet/data';
import type { SubtaskItem } from '@tradeoutlet/shared';
import { errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useMembers, useTeams, useWorkspaceId } from '@/lib/hooks';
import { hasReminder, reminderText, relatesTo } from '@/features/tasks/taskList';
import { useUi } from '@/lib/store';
import { Spinner } from '@/components/ui';
import { ukToday } from '@/features/tasks/privateTasks';
import { useAckList } from '@/features/acks/AcksPage';
import { SwipeRow } from './SwipeRow';
import { ChecklistRow } from './ChecklistRow';
import { MobileStartChecklists } from './StartChecklists';
import { HoldCircle } from './HoldCircle';
import { ChipRow } from './ChipRow';
import { BottomSheet } from './BottomSheet';
import { primaryBtn } from './FeedCards';
import { DateRangePresets } from '@/components/ui/DateRangeFilter';
import { presetValue, rangeLabel, type DateRangeValue } from '@/lib/dateRange';
import { PriorityFlag } from './PriorityFlag';
import { NewChecklistButton } from './ChecklistTemplates';
import { NewTaskModal } from '@/features/tasks/TasksArea';
import { PHONE_NEW_BTN } from './headerNew';


export function useCompleteWithUndo() {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const done = useSetTaskDone(ws);
  // mutateAsync, not mutate callbacks: the Undo must still appear if the row (or the page) has gone by the time it lands.
  return (t: Pick<TaskListItem, 'id' | 'version' | 'key' | 'title'>) => done.mutateAsync({ task: t, done: true }).then(
    updated => toast(`Done: ${t.title}`, 'success', { label: 'Undo', run: () => { done.mutateAsync({ task: updated, done: false }).catch(e => toast(errorMessage(e), 'error')); } }),
    e => toast(errorMessage(e), 'error'),
  );
}

/** t333u: the subtasks of an expanded row: indented, compact; hold the circle to complete, tap to open. */
function SubtaskRows({ parentId, today }: { parentId: string; today: string }) {
  const ws = useWorkspaceId();
  const openTask = useUi(s => s.openTask);
  const { byId } = useMembers();
  const q = useSubtasks(ws, parentId);
  const complete = useCompleteWithUndo();
  const reopen = useReopenWithUndo();
  const note = (text: string, tone = 'text-slate-500') => <p className={clsx('border-b border-slate-100 py-2 pl-[3.25rem] pr-4 text-xs dark:border-slate-800', tone)} data-testid="m-subtask-note">{text}</p>;
  if (q.isLoading) return note('Loading subtasks…');
  if (q.error) return note(errorMessage(q.error), 'text-rose-700');
  if (!q.data?.length) return note('No subtasks you can see.');
  return (
    <div data-testid="m-subtasks" data-parent={parentId}>
      {q.data.map((s: SubtaskItem) => {
        const done = Boolean(s.completed_at);
        const due = done ? null : taskDueLabel(s.due_date, today, s.due_time);
        const who = s.assignee_ids?.length ? `${byId.get(s.assignee_ids[0]!)?.display_name ?? 'Someone'}${s.assignee_ids.length > 1 ? ` +${s.assignee_ids.length - 1}` : ''}` : null;
        const meta = [who, due ? (due.startsWith('Overdue') ? due : `Due ${due.replace(/^Today/, 'today').replace(/^Tomorrow/, 'tomorrow')}`) : null].filter(Boolean).join(' · ');
        return (
          <div key={s.id} className="flex min-h-[48px] items-center border-b border-slate-100 bg-slate-50/60 pl-8 dark:border-slate-800 dark:bg-slate-900/40" data-testid="m-subtask-row">
            <HoldCircle done={done} size="sm" onDone={() => void (done ? reopen(s) : complete(s))} label={done ? `Reopen ${s.title}` : `Mark as done: ${s.title}`} testId="m-subtask-check" />
            <button type="button" onClick={() => openTask(s.id)} className="flex min-w-0 flex-1 flex-col py-1.5 pr-4 text-left" data-testid="m-subtask-open">
              <span className={clsx('truncate text-sm', done ? 'text-slate-500 line-through decoration-slate-400' : 'text-slate-900 dark:text-white')}>{s.title}</span>
              {meta && <span className={clsx('truncate text-xs', due?.startsWith('Overdue') ? 'font-semibold text-rose-700 dark:text-rose-400' : 'text-slate-500')}>{meta}</span>}
            </button>
          </div>
        );
      })}
    </div>
  );
}

function TaskRow({ t, today, onToggle }: { t: TaskListItem; today: string; onToggle: () => void }) {
  const openTask = useUi(s => s.openTask);
  const expanded = useExpandedTasks();
  const open = t.subtask_count > 0 && expanded.isOpen(t.id);
  const { byId: teams } = useTeams();
  const done = Boolean(t.completed_at);
  const due = done ? null : taskDueLabel(t.due_date, today, t.due_time);
  const overdue = due?.startsWith('Overdue');
  const body = (
    <div className="flex min-h-[60px] items-center border-b border-slate-100 pl-1.5 dark:border-slate-800">
      <HoldCircle done={done} onDone={onToggle} label={done ? `Reopen ${t.title}` : `Mark as done: ${t.title}`} testId="m-task-check" />
      <button type="button" onClick={() => openTask(t.id)} className="flex min-w-0 flex-1 items-center gap-2 py-2 pr-3 text-left" data-testid="m-task-open">
        <span className="min-w-0 flex-1">
          <span className={clsx('line-clamp-2 text-[15px] leading-snug', done ? 'text-slate-500 line-through decoration-slate-400' : t.unread ? 'font-semibold text-slate-900 dark:text-white' : 'font-normal')} data-testid="m-task-title" data-unread={t.unread && !done ? 'true' : undefined}>{t.title}</span>
          {t.unread && !done && <span className="sr-only">(unread)</span>}
          {/* t293u: one muted line saying what it relates to: "Peak trading 2026 · Due Fri 23 Oct", "Private · 🔔", "From chat · #general". */}
          <span className="mt-0.5 flex flex-wrap items-center gap-x-1 text-xs text-slate-500" data-testid="m-task-meta">
            {[
              done && t.completed_at ? <span key="done">Done {DateTime.fromISO(t.completed_at).setZone('Europe/London').toFormat('ccc d LLL')}</span> : null,
              ...relatesTo(t, teams).map(p => <span key={p.key} className="inline-flex min-w-0 items-center truncate">{p.key === 'private' && <Lock className="mr-0.5 inline h-3 w-3" aria-hidden />}{p.text}</span>),
              due ? <span key="due" className={clsx(overdue && 'font-semibold text-rose-700 dark:text-rose-400')} data-testid="m-task-due">{overdue ? due : `Due ${due.replace(/^Today/, 'today').replace(/^Tomorrow/, 'tomorrow')}`}</span> : null,
              /* t218u: only High and Urgent show in a list row, as a coloured flag with dark text. */
              !done && (t.priority === 'high' || t.priority === 'urgent') ? <span key="pri" className="inline-flex items-center gap-0.5 font-medium text-slate-700 dark:text-slate-200" data-testid="m-task-priority"><PriorityFlag priority={t.priority} className="h-3 w-3" />{PRIORITY_LABEL[t.priority]}</span> : null,
              hasReminder(t) ? <span key="bell" aria-label={reminderText(t) ?? 'Has a reminder'} title={reminderText(t) ?? undefined} data-testid="m-task-reminder">🔔</span> : null,
            ].filter(Boolean).flatMap((el, i) => (i ? [<span key={`sep${i}`} aria-hidden>·</span>, el] : [el]))}
          </span>
        </span>
        {!t.subtask_count && <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />}
      </button>
      {t.subtask_count > 0 && (
        <button type="button" onClick={() => expanded.toggle(t.id)} aria-expanded={open} data-testid="m-task-expand"
          aria-label={`${open ? 'Hide' : 'Show'} subtasks: ${t.subtask_done_count} of ${t.subtask_count} done`}
          className="flex h-11 shrink-0 items-center gap-1 pl-1 pr-3 text-xs tabular-nums text-slate-500">
          <span data-testid="m-task-subtask-count">{t.subtask_done_count}/{t.subtask_count}</span>
          <ChevronDown className={clsx('h-4 w-4 text-slate-400 transition-transform', open && 'rotate-180')} aria-hidden />
        </button>
      )}
    </div>
  );
  const row = done ? <div data-testid="m-task-row">{body}</div> : <SwipeRow onSwipe={onToggle} label="Done" testId="m-task-row">{body}</SwipeRow>;
  return open ? <>{row}<SubtaskRows parentId={t.id} today={today} /></> : row;
}

/** t196u: reopen a done task (from the Done chip), with Undo. */
export function useReopenWithUndo() {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const set = useSetTaskDone(ws);
  return (t: Pick<TaskListItem, 'id' | 'version' | 'key' | 'title'>) => set.mutateAsync({ task: t, done: false }).then(
    updated => toast(`Reopened: ${t.title}`, 'success', { label: 'Undo', run: () => { set.mutateAsync({ task: updated, done: true }).catch(e => toast(errorMessage(e), 'error')); } }),
    e => toast(errorMessage(e), 'error'),
  );
}

const FILTER_KEY = 'collab.m.tasks.filter';
const SEG_KEY = 'collab.m.tasks.segment';
type Seg = 'tasks' | 'checklists';
const DUE_KEY = 'm.checklists.mine.due';
const chipCls = (on: boolean) => clsx('flex h-8 items-center gap-1 rounded-full border px-3 text-[13px] font-medium',
  on ? 'border-black bg-black text-white dark:border-white dark:bg-white dark:text-black' : 'border-slate-300 text-slate-700 dark:border-slate-700 dark:text-slate-200');

/** Swipe left / right on the list switches segment (vertical scroll still wins). */
function useSwipeSeg(seg: Seg, set: (s: Seg) => void) {
  const start = useRef<{ x: number; y: number } | null>(null);
  return {
    onTouchStart: (e: React.TouchEvent) => { start.current = { x: e.touches[0]!.clientX, y: e.touches[0]!.clientY }; },
    onTouchEnd: (e: React.TouchEvent) => {
      const s0 = start.current; start.current = null; if (!s0) return;
      const dx = e.changedTouches[0]!.clientX - s0.x; const dy = e.changedTouches[0]!.clientY - s0.y;
      if (Math.abs(dx) > 80 && Math.abs(dx) > Math.abs(dy) * 1.5) { if (dx < 0 && seg === 'tasks') set('checklists'); else if (dx > 0 && seg === 'checklists') set('tasks'); }
    },
  };
}

/** t214u: My checklists, filtered; a row opens the phone checklist runner. */
function ChecklistList({ q, list, filter, query, today, swipe, empty }: { q: { isLoading: boolean; error: unknown }; list: ChecklistRun[]; filter: MyChecklistFilter; query: string; today: string; swipe: ReturnType<typeof useSwipeSeg>; empty: string }) {
  const rows = sortChecklists(list.filter(r => checklistMatches(r, filter, today, query)), filter);
  return (
    <div className="min-h-0 flex-1 overflow-y-auto" data-testid="m-checklists-scroll" {...swipe}>
      {!query.trim() && <MobileStartChecklists />}
      {q.isLoading ? <div className="flex justify-center p-6"><Spinner /></div> : q.error ? <p className="p-4 text-sm text-rose-700">{errorMessage(q.error)}</p>
        : !rows.length ? <p className="px-4 py-8 text-center text-sm text-slate-500" data-testid="m-checklists-empty">{query.trim() ? `No checklists match “${query.trim()}”` : filter === 'completed' ? 'Nothing completed yet.' : empty || 'No checklists to do.'}</p>
        : rows.map(r => <ChecklistRow key={r.id} r={r} />)}
      <Link to="/checklists" data-testid="m-all-checklists" className="flex h-12 items-center justify-center text-sm font-medium text-slate-600 dark:text-slate-300">All checklists</Link>
    </div>
  );
}
const readFilter = (): MyTaskFilter => { try { const v = sessionStorage.getItem(FILTER_KEY); return MY_TASK_FILTERS.some(f => f.id === v) ? v as MyTaskFilter : 'all'; } catch { return 'all'; } };

function OtherRow({ to, icon, label, n }: { to: string; icon: React.ReactNode; label: string; n: number }) {
  return (
    <Link to={to} className="flex min-h-[52px] items-center gap-3 border-b border-slate-100 px-4 active:bg-slate-100 dark:border-slate-800 dark:active:bg-slate-800">
      <span className="text-slate-500" aria-hidden>{icon}</span><span className="flex-1 text-[15px]">{label}</span>
      <span className="text-xs font-semibold tabular-nums text-slate-500 dark:text-slate-400">{n}</span>
      <ChevronRight className="h-4 w-4 text-slate-400" aria-hidden />
    </Link>
  );
}

export default function MobileTasksPage() {
  const ws = useWorkspaceId();
  const { userId } = useAuth();
  const { members } = useMembers();
  const [params, setParams] = useSearchParams();
  const openTask = useUi(s => s.openTask);
  const [sheet, setSheet] = useState(params.get('new') === '1');
  useEffect(() => { if (params.get('new') === '1') { setSheet(true); params.delete('new'); setParams(params, { replace: true }); } }, [params]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = params.get('task'); if (t) openTask(t); }, [params, openTask]);
  // t194u: the box at the top only searches; t196u: chips filter (remembered for the session) and combine with it.
  const [query, setQuery] = useState('');
  const [filter, setFilterState] = useState<MyTaskFilter>(readFilter);
  const setFilter = (f: MyTaskFilter) => { setFilterState(f); try { sessionStorage.setItem(FILTER_KEY, f); } catch { /* private mode */ } };
  const q = useMyOpenTasks(ws);
  const doneQ = useMyDoneTasks(ws);
  const complete = useCompleteWithUndo();
  const reopen = useReopenWithUndo();
  const today = ukToday();
  const names = useMemo(() => new Map(members.map(m => [m.user_id, m.display_name])), [members]);
  const match = (t: TaskListItem) => taskMatches(t as SearchableTask, query, id => names.get(id));
  const open = (q.data ?? []).filter(match);
  const doneList = (doneQ.data ?? []).filter(match);
  const groups = filterMyTasks(open, doneList, filter, today);
  const counts = myTaskCounts(open, doneQ.data ? doneList : undefined, today);
  const active = filter === 'done' ? doneQ : q;
  const checklists = useMyChecklists(ws);
  const issues = useMyIssues(ws, 'open');
  const acks = useAckList('pending');
  const assigned = (issues.data ?? []).filter(i => isIssueAssignee(i, userId)).length;
  const pending = acks.data?.items.length ?? 0;
  const list = useRef<HTMLDivElement>(null);
  useScrollRestoration(list, { ready: Boolean(q.data) });
  const searching = query.trim().length > 0;
  // t214u: the segment lives in the URL (/tasks, /tasks/checklists) and is remembered for the session.
  const loc = useLocation();
  const navigate = useNavigate();
  const seg: Seg = loc.pathname.replace(/\/+$/, '').endsWith('/tasks/checklists') ? 'checklists' : 'tasks';
  const setSeg = (next: Seg) => { if (next === seg) return; try { sessionStorage.setItem(SEG_KEY, next); } catch { /* private mode */ } setQuery(''); navigate({ pathname: next === 'checklists' ? '/tasks/checklists' : '/tasks', search: '' }, { replace: true }); };
  useEffect(() => {
    let saved: string | null = null; try { saved = sessionStorage.getItem(SEG_KEY); } catch { /* private mode */ }
    if (seg === 'tasks' && saved === 'checklists' && !params.get('new') && !params.get('task') && loc.pathname === '/tasks') navigate('/tasks/checklists', { replace: true });
    else try { sessionStorage.setItem(SEG_KEY, seg); } catch { /* private mode */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const swipe = useSwipeSeg(seg, setSeg);
  const [clFilter, setClFilter] = useState<MyChecklistFilter>('outstanding');
  // t329u: the date choice (All = the usual yesterday-and-today list), kept for the session. Ranges come from the preset each day; the tab count stays the usual list's.
  const [due, setDueState] = useState<DateRangeValue>(() => { try { const v = JSON.parse(sessionStorage.getItem(DUE_KEY) ?? 'null') as DateRangeValue | null; if (v?.preset) return v; } catch { /* private mode */ } return presetValue('any'); });
  const setDue = (v: DateRangeValue) => { setDueState(v); try { sessionStorage.setItem(DUE_KEY, JSON.stringify(v)); } catch { /* private mode */ } };
  const [dueOpen, setDueOpen] = useState(false);
  const dueRange = checklistDueRange(due, today);
  const dueQ = useMyChecklists(ws, dueRange);
  const clQ = dueRange ? dueQ : checklists;
  const nowIso = new Date().toISOString();
  const clList = (clQ.data ?? []).filter(r => checklistDueMatches(r, due.preset, nowIso));
  const segCount = checklistCounts(checklists.data ?? [], today).outstanding;
  const clCounts = checklistCounts(clList, today);
  const dueLabel = rangeLabel(due, undefined, CHECKLIST_DUE_LABELS);
  return (
    <div className="flex h-full flex-col bg-white dark:bg-slate-950" data-testid="m-tasks">
      {/* t214u: switch on top, then search (+ on My tasks), then the chips row (t215u ChipRow). */}
      <div className="shrink-0 space-y-2 px-4 pb-2 pt-2">
        <div className="grid grid-cols-2 rounded-xl bg-slate-100 p-0.5 dark:bg-slate-800" role="tablist" aria-label="Tasks or checklists" data-testid="m-tasks-segments">
          {(['tasks', 'checklists'] as const).map(id => (
            <button key={id} type="button" role="tab" aria-selected={seg === id} onClick={() => setSeg(id)} data-testid={`m-seg-${id}`}
              className={clsx('flex h-9 items-center justify-center gap-1 rounded-[10px] text-sm font-semibold', seg === id ? 'bg-white text-ink shadow-sm dark:bg-slate-950 dark:text-white' : 'text-slate-600 dark:text-slate-300')}>
              {id === 'tasks' ? 'My tasks' : 'My checklists'}{id === 'checklists' && segCount > 0 && <span className="tabular-nums text-slate-500" data-testid="m-seg-checklists-count">({segCount})</span>}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden />
            <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder={seg === 'tasks' ? 'Search tasks' : 'Search checklists'} aria-label={seg === 'tasks' ? 'Search tasks' : 'Search checklists'} enterKeyHint="search" autoComplete="off" data-testid="m-tasks-search"
              className="h-11 w-full rounded-xl border border-slate-300 bg-white pl-9 pr-10 text-base placeholder:text-slate-400 focus:border-slate-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900 [&::-webkit-search-cancel-button]:hidden" />
            {searching && <button type="button" onClick={() => setQuery('')} aria-label="Clear search" data-testid="m-tasks-search-clear"
              className="absolute right-0 top-0 flex h-11 w-11 items-center justify-center text-slate-500"><X className="h-4 w-4" /></button>}
          </div>
          {seg === 'tasks' && <button type="button" onClick={() => setSheet(true)} aria-label="New task" data-testid="m-tasks-new"
            className={PHONE_NEW_BTN}><Plus className="h-5 w-5" strokeWidth={2.5} /></button>}
          {seg === 'checklists' && <button type="button" onClick={() => setDueOpen(true)} aria-haspopup="dialog" aria-label={`Dates: ${dueLabel}`} data-testid="m-checklists-due"
            className={clsx('flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white ring-1 ring-inset active:bg-slate-100 dark:bg-slate-900', due.preset === 'any' ? 'text-slate-600 ring-slate-300 dark:text-slate-300 dark:ring-slate-700' : 'text-ink ring-ink dark:text-white dark:ring-white')}><CalendarDays className="h-5 w-5" aria-hidden /></button>}
          {seg === 'checklists' && <NewChecklistButton />}
        </div>
        {seg === 'tasks' ? (
          <ChipRow bleed role="tablist" aria-label="Filter tasks" data-testid="m-tasks-filters">
            {MY_TASK_FILTERS.map(f => {
              const n = counts[f.id]; const on = filter === f.id;
              return (
                <button key={f.id} type="button" role="tab" aria-selected={on} onClick={() => setFilter(f.id)} data-testid={`m-tasks-filter-${f.id}`} className={chipCls(on)}>
                  {f.label}
                  {n !== undefined && n > 0 && f.id !== 'done' && <span className={clsx('tabular-nums', f.id === 'overdue' ? on ? 'text-rose-300 dark:text-rose-700' : 'text-rose-700 dark:text-rose-400' : 'opacity-70')}>{n}</span>}
                </button>
              );
            })}
          </ChipRow>
        ) : (
          <ChipRow bleed role="tablist" aria-label="Filter checklists" data-testid="m-checklists-filters">
            {MY_CHECKLIST_FILTERS.map(f => {
              const on = clFilter === f.id; const n = clCounts[f.id];
              return (
                <button key={f.id} type="button" role="tab" aria-selected={on} onClick={() => setClFilter(f.id)} data-testid={`m-checklists-filter-${f.id}`} className={chipCls(on)}>
                  {f.label}{n > 0 && f.id !== 'completed' && <span className={clsx('tabular-nums', f.id === 'overdue' ? on ? 'text-rose-300 dark:text-rose-700' : 'text-rose-700 dark:text-rose-400' : 'opacity-70')}>{n}</span>}
                </button>
              );
            })}
          </ChipRow>
        )}
        {seg === 'checklists' && due.preset !== 'any' && (
          <p className="-mt-0.5 flex items-center gap-2 text-sm text-slate-500" data-testid="m-checklists-due-note">Dates: {dueLabel}
            <button type="button" onClick={() => setDue(presetValue('any'))} className="text-slate-600 underline dark:text-slate-300" data-testid="m-checklists-due-clear">Clear</button></p>
        )}
      </div>
      <BottomSheet open={dueOpen} onClose={() => setDueOpen(false)} title="Dates" testId="m-checklists-due-sheet">
        <DateRangePresets className="px-4 pb-4" value={due} presets={[...CHECKLIST_DUE_PRESETS]} labels={CHECKLIST_DUE_LABELS} label="Due" data-testid="m-checklists-due-pick"
          onChange={v => { setDue(v); if (v.preset !== 'custom') setDueOpen(false); }} />
        {due.preset === 'custom' && <div className="px-4 pb-2"><button type="button" className={clsx(primaryBtn, 'w-full')} onClick={() => setDueOpen(false)} data-testid="m-checklists-due-done">Done</button></div>}
      </BottomSheet>
      {seg === 'checklists' ? <ChecklistList q={clQ} list={clList} filter={clFilter} query={query} today={today} swipe={swipe} empty={checklistDueEmpty(due.preset)} /> : (
      <div ref={list} className="min-h-0 flex-1 overflow-y-auto" data-testid="m-tasks-scroll" {...swipe}>
        {active.isLoading ? <div className="flex justify-center p-6"><Spinner /></div> : active.error ? <p className="p-4 text-sm text-rose-700">{errorMessage(active.error)}</p>
          : !groups.length ? <p className="px-4 py-8 text-center text-sm text-slate-500" data-testid="m-tasks-empty">{searching ? `No tasks match “${query.trim()}”` : filter === 'done' ? 'Nothing done yet.' : filter === 'all' ? 'Nothing on your list.' : 'Nothing here.'}</p>
          : groups.map(g => (
            <section key={g.id} aria-label={g.label} data-testid={`m-task-group-${g.id}`}>
              <h2 className={clsx('sticky top-0 z-[1] flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide dark:border-slate-800 dark:bg-slate-900', g.id === 'overdue' ? 'text-rose-700 dark:text-rose-400' : 'text-slate-500')}>
                {g.label}<span className="font-medium normal-case tracking-normal">{g.tasks.length}</span>
              </h2>
              {g.tasks.map(t => <TaskRow key={t.id} t={t} today={today} onToggle={() => (t.completed_at ? reopen(t) : complete(t))} />)}
            </section>
          ))}
        {!searching && filter === 'all' && (assigned > 0 || pending > 0) && (
          <section className="mt-4" aria-label="Also for you">
            <h2 className="bg-slate-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-900">Also for you</h2>
            {assigned > 0 && <OtherRow to="/incidents" icon={<Siren className="h-5 w-5" />} label="Issues assigned to you" n={assigned} />}
            {pending > 0 && <OtherRow to="/acknowledgements" icon={<ShieldCheck className="h-5 w-5" />} label="To acknowledge" n={pending} />}
          </section>
        )}
        <Link to="/tasks?full=1" className="flex h-12 items-center justify-center text-sm font-medium text-slate-600 dark:text-slate-300">All tasks and filters</Link>
      </div>)}
      {/* t194u: the + opens the full new-task form (title, for whom, due, building, private…), not a one-line capture. */}
      {sheet && <NewTaskModal initialPrivate onClose={() => setSheet(false)} />}
    </div>
  );
}
